import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import {
  EMPTY,
  Observable,
  Subject,
  catchError,
  combineLatest,
  filter,
  finalize,
  merge,
  of,
  switchMap,
  takeUntil,
  takeWhile,
  tap,
  timer,
} from 'rxjs';
import { baseMockRespondentAnswer } from '../../mocks/data-capture.mocks';
import { respondentsMock } from '../../mocks/respondents.mocks';
import {
  DataCaptureSummaryStatus,
  IDataCaptureSheet,
  IDataCaptureSummaryCell,
  IDataCaptureSummaryQuote,
  IDataCaptureSummaryTheme,
  IDiscussionGuideQuestion,
  IRespondent,
  IRespondentWithAnswers,
  IRespondentAnswer,
  IRespondentAnswerReference,
  SuggestionStatus,
} from '../../models/data-capture.models';
import { DataCaptureService } from '../../services/data-capture.service';
import {
  assignDepthAndNotation,
  orderQuestionsForDisplay,
} from '../../utils/question-numbering.util';
import { DeleteRespondentConfirmationComponent } from '../delete-respondent-confirmation/delete-respondent-confirmation.component';
import { RegenerateAnswerConfirmationComponent } from '../regenerate-answer-confirmation/regenerate-answer-confirmation.component';
import { SheetFilterComponent } from '../sheet-filter/sheet-filter.component';
import { ViewRespondentComponent } from '../view-respondent/view-respondent.component';

type OpenCitation = {
  respondent: IRespondent;
  citation: IRespondentAnswerReference;
};

export type ExtendedRespondentAnswer = IRespondentAnswer & {
  editedAnswer?: string;
  editing: boolean;
  loading: boolean;
};

export interface IRespondentUI extends IRespondent {
  answers: Record<string, ExtendedRespondentAnswer>;
}

interface IGroupedTheme {
  theme: string;
  questions: (IDiscussionGuideQuestion & {
    numberNotation: string;
  })[];
  suggestedQuestions: (IDiscussionGuideQuestion & {
    numberNotation: string;
  })[];
}

type DisplayQuestionMap = Record<
  string,
  (IDiscussionGuideQuestion & {
    numberNotation: string;
  })[]
>;

@Component({
  selector: 'app-data-sheet',
  templateUrl: './data-sheet.component.html',
  styleUrls: ['./data-sheet.component.scss'],
  standalone: false,
})
export class DataSheetComponent implements OnInit, OnChanges, OnDestroy {
  @Input() dataCaptureSheetId: string;
  @Input() sheet: IDiscussionGuideQuestion[] = [];
  @Input() sheetHeaders: IDataCaptureSheet['additional_export_headers'];
  @Input() refreshDataTrigger = false;
  @Input() answerClickThrough = false;
  @Input() respondentClickThrough = false;
  @Input() answer_format: IDataCaptureSheet['answer_format'] = 'summarised';
  @Input() themeFormChanges$: Observable<boolean> = EMPTY;

  @Output() openCitation = new EventEmitter<OpenCitation>();
  @Output() sheetUpdated = new EventEmitter<IDataCaptureSheet>();

  private destroy$ = new Subject<boolean>();
  private pollSheetData$ = new Subject<boolean>();

  respondents: IRespondentUI[] = [];
  filteredRespondents: IRespondentUI[] = [];
  summaries: Record<string, IDataCaptureSummaryCell> = {};
  displayQuestionsByTheme: DisplayQuestionMap = {};

  groupedThemes: IGroupedTheme[];

  refreshedDataTime: number;

  tableFilters: string[];

  loading = true;
  polling = true;
  summaryStatus = DataCaptureSummaryStatus;
  activeAttributionThemeId: string | undefined;
  highlightedAttributedAnswerKeys = new Set<string>();

  get filterApplied(): boolean {
    return (
      this.tableFilters &&
      !this.respondents.every((r) => this.tableFilters.includes(r.respondentId))
    );
  }

  get showSummaryRow(): boolean {
    if (!this.dataCaptureSheetId) {
      return false;
    }

    return Object.values(this.summaries || {}).some(
      (summary) =>
        summary?.status === DataCaptureSummaryStatus.Processing ||
        summary?.status === DataCaptureSummaryStatus.Processed
    );
  }

  constructor(
    private dataCaptureService: DataCaptureService,
    private dialog: MatDialog,
    private toastr: ToastrService
  ) {}

  ngOnInit() {
    this.groupedThemes = this.groupQuestionsByTheme();

    if (this.dataCaptureSheetId) {
      this.refreshData();
    } else {
      this.respondents = this.mapMockRespondents();
      this.filteredRespondents = this.respondents;
      this.summaries = {};
      this.loading = false;
    }

    this.themeFormChanges$
      .pipe(
        filter(() => !this.dataCaptureSheetId),
        tap(() => {
          this.groupedThemes = this.groupQuestionsByTheme();
          this.respondents = this.mapMockRespondents();
          this.filteredRespondents = this.respondents;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.sheet && !changes.sheet.firstChange) {
      this.groupedThemes = this.groupQuestionsByTheme();
    }

    if (changes.dataCaptureSheetId && !changes.dataCaptureSheetId.firstChange) {
      const { currentValue, previousValue } = changes.dataCaptureSheetId;
      if (currentValue !== previousValue) {
        this.refreshData();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();

    this.pollSheetData$.next(true);
    this.pollSheetData$.complete();
  }

  refreshData() {
    this.refreshDataInternal(false, true).subscribe();
  }

  copy(answer: IRespondentAnswer) {
    navigator.clipboard.writeText(answer.answer);
    this.toastr.success('Answer copied to clipboard');
  }

  viewRespondent(respondent: IRespondent) {
    if (!this.respondentClickThrough) {
      return;
    }

    interface IRespondentData {
      respondentId: string;
      respondents: IRespondentUI[];
      groupedThemes: DataSheetComponent['groupedThemes'];
    }

    interface IRespondentResponse extends Partial<IRespondentAnswer> {
      respondent_id: string;
      question_id: string;
    }

    this.dialog
      .open<ViewRespondentComponent, IRespondentData, IRespondentResponse[]>(
        ViewRespondentComponent,
        {
          width: '1100px',
          height: '750px',
          autoFocus: false,
          data: {
            respondentId: respondent.respondentId,
            respondents: this.respondents,
            groupedThemes: this.groupedThemes,
          },
        }
      )
      .afterClosed()
      .pipe(
        filter((res) => res && !!res.length),
        tap(() => (this.loading = true)),
        switchMap((answers) =>
          this.dataCaptureService.updateRespondentAnswer(
            answers.map((a) => ({
              ...a,
              answer_method: 'manual',
              answer_format: this.answer_format,
            }))
          )
        ),
        tap((answers) => {
          this.respondents = this.respondents.map((r) => {
            const updatedAnswers = answers.filter(
              (a) => a.respondent_id === r.respondentId
            );

            if (!updatedAnswers.length) {
              return r;
            }

            const updatedAnswersMap = updatedAnswers.reduce<
              Record<string, ExtendedRespondentAnswer>
            >(
              (acc, curr) => ({
                ...acc,
                [curr.question_id]: { ...curr, editing: false, loading: false },
              }),
              {}
            );

            return {
              ...r,
              answers: { ...r.answers, ...updatedAnswersMap },
            };
          });
          this.filteredRespondents = this.respondents;
        }),
        switchMap((answers) =>
          this.dataCaptureSheetId
            ? this.refreshSummariesAndPoll(answers.map((a) => a.question_id))
            : EMPTY
        ),
        catchError(() => {
          this.toastr.error('Please try again', 'Error updating answers');
          return [];
        }),
        finalize(() => (this.loading = false))
      )
      .subscribe();
  }

  viewCitation(respondent: IRespondent, citation: IRespondentAnswerReference) {
    this.openCitation.emit({ respondent, citation });
  }

  viewSummaryCitation(
    quote: IDataCaptureSummaryQuote,
    citation: IRespondentAnswerReference
  ) {
    const respondent = this.respondents.find(
      (r) => r.respondentId === quote.respondent_id
    );

    if (!respondent) {
      return;
    }

    this.viewCitation(respondent, citation);
  }

  edit(answer: ExtendedRespondentAnswer) {
    if (answer.loading) {
      return;
    }

    answer.editedAnswer = answer.answer;
    answer.editing = true;
  }

  cancelEdit(answer: ExtendedRespondentAnswer) {
    if (answer.loading) {
      return;
    }

    answer.editedAnswer = '';
    answer.editing = false;
  }

  saveAnswer(answer: ExtendedRespondentAnswer) {
    answer.loading = true;
    answer.answer = answer.editedAnswer;
    answer.editing = false;

    this.dataCaptureService
      .updateRespondentAnswer([
        {
          respondent_id: answer.respondent_id,
          question_id: answer.question_id,
          answer: answer.editedAnswer,
          answer_method: 'manual',
          answer_format: answer.answer_format,
        },
      ])
      .pipe(
        tap(() => {
          answer.answer_method = 'manual';
          answer.created_at = new Date().toISOString();
        }),
        switchMap(() =>
          this.dataCaptureSheetId
            ? this.refreshSummariesAndPoll([answer.question_id])
            : EMPTY
        ),
        catchError((err) => {
          answer.editing = true;
          this.toastr.error('Please try again', 'Error saving answer');
          return err;
        }),
        finalize(() => (answer.loading = false))
      )
      .subscribe();
  }

  regenerateAnswer(answer: ExtendedRespondentAnswer, respondent: IRespondent) {
    this.dialog
      .open<RegenerateAnswerConfirmationComponent, undefined, boolean>(
        RegenerateAnswerConfirmationComponent,
        {
          width: '500px',
          autoFocus: false,
        }
      )
      .afterClosed()
      .pipe(
        filter((res) => !!res),
        tap(() => (answer.loading = true)),
        switchMap(() =>
          this.dataCaptureService.regenerateRespondentAnswer({
            respondentId: answer.respondent_id,
            questionId: answer.question_id,
            type: respondent.type,
          })
        ),
        tap(() => {
          this.respondents = this.respondents.map((r) =>
            r.respondentId === answer.respondent_id
              ? {
                  ...r,
                  answers: { ...r.answers, [answer.question_id]: undefined },
                }
              : r
          );
          this.filteredRespondents = this.filterRespondents(this.tableFilters);
          this.markSummariesAsProcessing([answer.question_id]);
        }),
        catchError((err) => {
          this.toastr.error('Please try again', 'Error regenerating answer');
          return err;
        }),
        finalize(() => (answer.loading = false))
      )
      .subscribe();
  }

  deleteRespondent(respondent: IRespondent) {
    this.dialog
      .open<DeleteRespondentConfirmationComponent, IRespondent, boolean>(
        DeleteRespondentConfirmationComponent,
        {
          width: '500px',
          autoFocus: false,
          data: respondent,
        }
      )
      .afterClosed()
      .pipe(
        filter((res) => !!res),
        switchMap(() =>
          this.dataCaptureService.deleteExternalConnection(
            respondent.respondentId
          )
        ),
        tap(() => {
          this.respondents = this.respondents.filter(
            (r) => r.respondentId !== respondent.respondentId
          );
          this.filteredRespondents = this.filteredRespondents.filter(
            (r) => r.respondentId !== respondent.respondentId
          );
          this.markSummariesAsProcessing(this.getSheetQuestionIds());
        }),
        switchMap(() =>
          this.dataCaptureSheetId
            ? this.dataCaptureService.getSheet(this.dataCaptureSheetId).pipe(
                tap((updatedSheet) => {
                  this.syncSheetState(updatedSheet);
                  this.sheetUpdated.emit(updatedSheet);
                }),
                catchError(() => {
                  this.toastr.error(
                    'Please refresh the sheet to see updated suggestions',
                    'Respondent deleted'
                  );
                  return of(null);
                })
              )
            : of(null)
        ),
        catchError(() => {
          this.toastr.error('Please try again', 'Error deleting respondent');
          return EMPTY;
        })
      )
      .subscribe();
  }

  filter() {
    this.dialog
      .open<
        SheetFilterComponent,
        { respondents: IRespondentUI[]; selectedRespondentIds: string[] },
        string[]
      >(SheetFilterComponent, {
        width: '500px',
        autoFocus: false,
        data: {
          respondents: this.respondents,
          selectedRespondentIds: this.tableFilters,
        },
      })
      .afterClosed()
      .pipe(
        takeWhile((filteredRespondentIds) => !!filteredRespondentIds),
        tap((filteredRespondentIds) => {
          this.filteredRespondents = this.filterRespondents(
            filteredRespondentIds
          );

          this.tableFilters = filteredRespondentIds;
        })
      )
      .subscribe();
  }

  get hasSuggestedQuestions(): boolean {
    return this.groupedThemes?.some(
      (group) => group.suggestedQuestions.length > 0
    );
  }

  getDisplayQuestions(group: IGroupedTheme) {
    if (!this.dataCaptureSheetId) {
      return group.questions;
    }

    return this.displayQuestionsByTheme[group.theme] || group.questions;
  }

  updateSuggestionStatus(questionId: string, status: SuggestionStatus) {
    if (!this.dataCaptureSheetId) {
      return;
    }

    const previousSheet = this.sheet;
    this.sheet = this.sheet.map((question) =>
      question.question_id === questionId
        ? { ...question, suggestion_status: status }
        : question
    );
    this.groupedThemes = this.groupQuestionsByTheme();

    this.dataCaptureService
      .updateSuggestionStatus(this.dataCaptureSheetId, questionId, status)
      .pipe(
        tap((updatedSheet) => {
          this.syncSheetState(updatedSheet);
          this.sheetUpdated.emit(updatedSheet);
        }),
        catchError(() => {
          this.toastr.error(
            'Please try again',
            `Error ${status === 'approved' ? 'approving' : 'declining'} suggestion`
          );
          this.sheet = previousSheet;
          this.groupedThemes = this.groupQuestionsByTheme();
          return EMPTY;
        })
      )
      .subscribe();
  }

  trackRespondent(_: number, r: IRespondentUI) {
    return r.respondentId;
  }

  trackSummaryTheme(_: number, theme: IDataCaptureSummaryTheme) {
    return theme.title;
  }

  toggleThemeAttribution(
    questionId: string,
    theme: IDataCaptureSummaryTheme,
    themeIndex: number
  ) {
    const attributionThemeId = this.toAttributionThemeId(
      questionId,
      themeIndex
    );

    if (this.activeAttributionThemeId === attributionThemeId) {
      this.clearThemeAttributionHighlights();
      return;
    }

    this.activeAttributionThemeId = attributionThemeId;
    this.highlightedAttributedAnswerKeys = this.getThemeAttributionKeys(theme);
  }

  isThemeAttributionActive(questionId: string, themeIndex: number) {
    return (
      this.activeAttributionThemeId ===
      this.toAttributionThemeId(questionId, themeIndex)
    );
  }

  isAnswerAttributed(respondentId: string, questionId: string) {
    return this.highlightedAttributedAnswerKeys.has(
      `${respondentId}_${questionId}`
    );
  }

  private refreshDataInternal(
    retainEditingState: boolean,
    showLoading: boolean,
    forceSummaryProcessingQuestionIds: string[] = []
  ) {
    if (showLoading) {
      this.loading = true;
    }

    this.pollSheetData$.next(true);

    return combineLatest([
      this.fetchRespondents$(retainEditingState, showLoading),
      this.fetchSummaries$(forceSummaryProcessingQuestionIds),
    ]).pipe(switchMap(() => this.pollingLoop$()));
  }

  private refreshSummariesAndPoll(
    forceSummaryProcessingQuestionIds: string[] = []
  ) {
    this.pollSheetData$.next(true);

    return this.fetchSummaries$(forceSummaryProcessingQuestionIds).pipe(
      switchMap(() => this.pollingLoop$())
    );
  }

  private pollingLoop$() {
    return timer(120000, 120000).pipe(
      filter(() =>
        this.shouldReloadBasedOn(
          this.respondents,
          this.groupedThemes,
          this.summaries
        )
      ),
      tap(() => (this.polling = true)),
      switchMap(() =>
        merge(this.fetchRespondents$(true, false), this.fetchSummaries$())
      ),
      takeUntil(this.pollSheetData$)
    );
  }

  private fetchRespondents$(
    retainEditingStateForFetch: boolean,
    showLoading: boolean
  ) {
    return this.dataCaptureService
      .getRespondentsWithAnswersByDataCaptureSheetId(this.dataCaptureSheetId)
      .pipe(
        tap((respondents) => {
          this.respondents = this.mergeRespondentAnswers(
            respondents,
            retainEditingStateForFetch
          );
          this.filteredRespondents = this.filterRespondents(this.tableFilters);
          this.groupedThemes = this.groupQuestionsByTheme();
          this.polling = false;

          this.clearThemeAttributionHighlights();
          this.refreshedDataTime = Date.now();
          if (showLoading) {
            this.loading = false;
          }
        })
      );
  }

  private mergeRespondentAnswers(
    respondents: IRespondentWithAnswers[],
    retainEditingStateForFetch: boolean
  ): IRespondentUI[] {
    const priorRespondentMap = (this.respondents || []).reduce<
      Record<string, IRespondentUI>
    >((acc, curr) => ({ ...acc, [curr.respondentId]: curr }), {});

    return respondents.map((respondent) => {
      const priorRespondent = priorRespondentMap[respondent.respondentId];

      return {
        ...respondent,
        answers: Object.entries(respondent.answers).reduce((acc, [k, v]) => {
          const priorAnswer = priorRespondent?.answers[k];

          return {
            ...acc,
            [k]: {
              ...v,
              ...(retainEditingStateForFetch && !!priorAnswer?.editing
                ? {
                    editing: priorAnswer.editing,
                    editedAnswer: priorAnswer.editedAnswer,
                  }
                : { editing: false }),
              loading: false,
            },
          };
        }, {}),
      };
    });
  }

  private fetchSummaries$(forceProcessingQuestionIds: string[] = []) {
    if (!this.dataCaptureSheetId) {
      return EMPTY;
    }

    return this.dataCaptureService
      .getSummariesByDataCaptureSheetId(
        this.dataCaptureSheetId,
        this.getSheetQuestionIds()
      )
      .pipe(
        tap((summaries) => {
          this.summaries = summaries || {};
          if (forceProcessingQuestionIds.length) {
            this.markSummariesAsProcessing(forceProcessingQuestionIds);
          }
        })
      );
  }

  private shouldReloadBasedOn(
    respondents: IRespondentUI[],
    groupedThemes: IGroupedTheme[],
    summaries: Record<string, IDataCaptureSummaryCell>
  ) {
    return (
      this.hasMissingAnswers(respondents, groupedThemes) ||
      this.hasProcessingSummaries(summaries)
    );
  }

  private hasMissingAnswers(
    respondents: IRespondentUI[],
    groupedThemes: IGroupedTheme[]
  ) {
    const flattenedQuestions = groupedThemes.flatMap((theme) => [
      ...theme.questions,
      ...theme.suggestedQuestions,
    ]);

    return respondents.some((r) =>
      flattenedQuestions.some((q) => !r.answers[q.question_id])
    );
  }

  private hasProcessingSummaries(
    summaries: Record<string, IDataCaptureSummaryCell>
  ) {
    return Object.values(summaries || {}).some(
      (summary) => summary.status === DataCaptureSummaryStatus.Processing
    );
  }

  private filterRespondents(res: string[]) {
    if (!res) {
      return this.respondents;
    }

    return this.respondents.filter((r) => res.includes(r.respondentId));
  }

  private markSummariesAsProcessing(questionIds: string[]) {
    if (!this.dataCaptureSheetId || !questionIds.length) {
      return;
    }

    const uniqueQuestionIds = [...new Set(questionIds)];

    const summaries = uniqueQuestionIds.reduce(
      (acc, questionId) => ({
        ...acc,
        [questionId]: acc[questionId]
          ? {
              ...acc[questionId],
              status: DataCaptureSummaryStatus.Processing,
            }
          : {
              data_capture_sheet_id: this.dataCaptureSheetId,
              question_id: questionId,
              summary: null,
              status: DataCaptureSummaryStatus.Processing,
              created_at: '',
              created_by: '',
              updated_at: '',
              updated_by: '',
            },
      }),
      { ...this.summaries }
    );

    this.summaries = summaries;
    this.polling = true;
  }

  private getSheetQuestionIds() {
    return [
      ...new Set((this.sheet || []).map((question) => question.question_id)),
    ];
  }

  private syncSheetState(updatedSheet: IDataCaptureSheet) {
    this.sheet = updatedSheet.discussion_guide;
    this.sheetHeaders = updatedSheet.additional_export_headers;
    this.answer_format = updatedSheet.answer_format;
    this.groupedThemes = this.groupQuestionsByTheme();
    this.clearThemeAttributionHighlights();
  }

  private groupQuestionsByTheme() {
    if (!this.sheet) {
      this.displayQuestionsByTheme = {};
      return [];
    }

    const allVisible = this.sheet.filter(
      (q) => q.suggestion_status !== 'declined'
    );
    const themes = allVisible.map((question) => question.theme);
    const distinctThemes = [...new Set(themes)];
    const orderedVisible = distinctThemes.flatMap((theme) =>
      orderQuestionsForDisplay(
        allVisible.filter((question) => question.theme === theme)
      )
    );
    const annotatedVisible = assignDepthAndNotation(orderedVisible);

    this.displayQuestionsByTheme = distinctThemes.reduce<DisplayQuestionMap>(
      (acc, theme) => ({
        ...acc,
        [theme]: annotatedVisible
          .filter((question) => question.theme === theme)
          .map(({ depth, ...question }) => question),
      }),
      {}
    );

    return distinctThemes.map((theme) => {
      const themeQuestions = this.displayQuestionsByTheme[theme] || [];
      const staticQuestions = themeQuestions
        .filter((q) => q.suggestion_status !== 'suggested')
        .map((q) => ({ ...q }));
      const suggestedQuestions = themeQuestions
        .filter((q) => q.suggestion_status === 'suggested')
        .map((q) => ({ ...q }));

      const displayQuestions = this.dataCaptureSheetId
        ? staticQuestions
        : staticQuestions.length > 5
          ? staticQuestions.slice(0, 5).concat([
              {
                question_id: '-',
                question: '...',
                theme,
                label: '...',
                numberNotation: '...',
              },
            ])
          : staticQuestions;

      return {
        theme,
        questions: displayQuestions,
        suggestedQuestions: this.dataCaptureSheetId ? suggestedQuestions : [],
      };
    });
  }

  private mapMockRespondents() {
    const answers = this.sheet.reduce<Record<string, ExtendedRespondentAnswer>>(
      (acc, question, i) => ({
        ...acc,
        [question.question_id]: {
          ...baseMockRespondentAnswer,
          question_id: question.question_id,
          answer: this.getRandomLoremIpsum(i),
          editing: false,
          loading: false,
        },
      }),
      {}
    );

    return respondentsMock.map((respondent) => ({
      ...respondent,
      answers,
    }));
  }

  private getRandomLoremIpsum(index: number) {
    const loremIpsum = [
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit.',
      'Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
      'Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
      'Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.',
      'Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.',
    ];

    return loremIpsum[index % loremIpsum.length];
  }

  private toAttributionThemeId(questionId: string, themeIndex: number) {
    return `${questionId}::${themeIndex}`;
  }

  private clearThemeAttributionHighlights() {
    this.activeAttributionThemeId = undefined;
    this.highlightedAttributedAnswerKeys = new Set<string>();
  }

  private getThemeAttributionKeys(theme: IDataCaptureSummaryTheme) {
    const keys = theme.sub_themes.flatMap((subTheme) =>
      (subTheme.quotes || []).map(
        (quote) => `${quote.respondent_id}_${quote.question_id}`
      )
    );

    return new Set(keys);
  }
}
